"use strict";

import AppUtil from "/js/util/app-util.js";

/**
 * Global state variables. 'Static' class.
 */
export default class Global {

  // Feature flags
  static #isPrimerBrand = false;
  static #isProd = false;
  static #isDev = false;
  static #isLiveMode = false;
  static #cmsDevJson = null;

  // Configuration values
  static #displayMode = null;
  static #parentOrigin = null;
  static #lessonId = null;
  static #languageCode = "en"; // Note default value.
  static #partnerId = null;
  static #ctaLabel = null;
  static #ctaLink = null;
  static #minicourseData = null;

  static #user = null;
  static #lessonData = null;

  static #cardScale = 1;
  static #stackCenterX = 0;
  static #stackCenterY = 0;

  /**
   * Prints values that are set on app initialization, for troubleshooting.
   */
  static printValues() {
    clw('Global.isProd:', Global.isProd);
    cl('Global.isDev:', Global.isDev);
    cl('Global.isLiveMode:', Global.isLiveMode);
    cl('Global.cmsDevJson:', Global.cmsDevJson);
    cl('Global.displayMode:', Global.displayMode);
    cl('Global.parentOrigin:', Global.parentOrigin);
    cl('Global.lessonId:', Global.lessonId);
    cl('Global.languageCode:', Global.languageCode);
    cl('Global.partnerId:', Global.partnerId);
    cl('Global.ctaLabel/Link:', Global.ctaLabel, Global.ctaLink);
    cl('Global.minicourseData:', Global.minicourseData);
  }

  /** Is app in "dev mode", which may be used to set various dev conveniences. */
  static get isDev() {
    return Global.#isDev;
  }

  static set isDev(b) {
    Global.#isDev = b;
  }

  /** Is app on "production". */
  static get isProd() {
    return Global.#isProd;
  }

  static set isProd(b) {
    Global.#isProd = b;
  }

  /**
   * Should page use the Primer brand style. Else will default to "Google" style.
   * TODO: Finish implementing/testing primer style or else remove altogether.
   */
  static get isPrimerBrand() {
    if (Global.#isPrimerBrand === null) {
      Global.#isPrimerBrand = $("body").hasClass("brand");
    }
    return Global.#isPrimerBrand;
  }

  /**
   * "Enum"-like value:
   * - `overlay`: Widget iframe assumed to be full-bleed on top of parent page's content (ie, should show a global close button)
   * - `standalone`: Widget iframe assumed to be full-bleed in a dedicated tab (ie, no close global button)
   * - `inline`: Widget iframe assumed to be placed inline, in the parent page's document flow (ie, no close button and no scrollwheel functionality)
   */
  static get displayMode() {
    return Global.#displayMode;
  }

  static set displayMode(b) {
    Global.#displayMode = b;
  }

  /**
   * The parent frame's origin.
   * Needed for sending a `postMessage` to the parent page.
   */
  static get parentOrigin() {
    return Global.#parentOrigin;
  }

  static set parentOrigin(s) {
    Global.#parentOrigin = s;
  }


  static get hasCta() {
    return !!Global.#ctaLabel && !!Global.#ctaLink;
  }

  static get ctaLabel() {
    return Global.#ctaLabel;
  }

  static get ctaLink() {
    return Global.#ctaLink;
  }

  static setCta(label, url) {
    Global.#ctaLabel = label;
    Global.#ctaLink = url;
  }

  static get lessonId() {
    return Global.#lessonId;
  }

  static set lessonId(s) {
    Global.#lessonId = s;
  }

  static get minicourseData() {
    return Global.#minicourseData;
  }

  static set minicourseData(data) {
    if (data.lessonIds) {
      // Massage lesson ids if necessary.
      const f = (s) => (s.indexOf("lesson-") === 0) ? s : "lesson-" + s;
      data.lessonIds = data.lessonIds.map(f);
    }
    Global.#minicourseData = data;
  }

  /** The lesson's JSON data. */
  static get lessonData() {
    return Global.#lessonData;
  }

  static getInteractionById(interactionId) {
    for (const o of Global.lessonData.interactions) {
      if (o.interactionId === interactionId) {
        return o;
      }
    }
    return null;
  }

  static setAndMassageLessonData(data) {
    const takeawaysCard = AppUtil.getTakeawaysFromRawLessonData(data);
    AppUtil.massageLessonData(data);
    Global.#lessonData = data;
    Global.#lessonData.takeawaysCard = takeawaysCard;
  }

  /**
   * Lesson interaction data that originates from the CMS Editor.
   * The existence of this value puts the webpage in a special mode.
   */
  static get cmsDevJson() {
    return Global.#cmsDevJson;
  }

  static set cmsDevJson(json) {
    Global.#cmsDevJson = json;
  }

  static get cardScale() {
    return Global.#cardScale;
  }

  static set cardScale(scale) {
    Global.#cardScale = scale;
  }

  /** X center of stacks of cards. */
  static get stackCenterX() {
    return Global.#stackCenterX;
  }

  static set stackCenterX(x) {
    Global.#stackCenterX = x;
  }

  static get isLiveMode() {
    return Global.#isLiveMode;
  }

  static set isLiveMode(b) {
    Global.#isLiveMode = b;
  }

  static get user() {
    return Global.#user;
  }

  static set user(user) {
    Global.#user = user;
  }

  static get languageCode() {
    return Global.#languageCode;
  }

  static set languageCode(value) {
    value = value.toLowerCase();
    Global.#languageCode = value;
    $('body').attr('data-language', Global.#languageCode);
  }

  static get partnerId() {
    return Global.#partnerId;
  }

  static set partnerId(value) {
    Global.#partnerId = value;
  }

  /**
   * Describes what the current main top-level view is.
   * (`minicourse` | `lesson` | `done` | `recap` | null)
   *
   * TODO: Need to add a setter now too ("setCurrentViewType(main)") to replace functionality in AppUtil.updateBodyClass()
   *  And not infer the "state" based on body's css class.
   *
   * @return {String}
   */
  static get currentViewType() {
    const s = $("body").attr("class");
    if (!s) {
      return null;
    }
    let type = null;
    if (s.includes("isMinicourse")) {
      type = "minicourse";
    }
    if (s.includes("isLesson")) {
      type = "lesson";
    } else if (s.includes("isDone")) {
      type = "done";
    } else if (s.includes("isRecap")) {
      type = "recap";
    }
    return type;
  }

  static get fullYourPrimerUrl() {
    const contentId = this.#minicourseData ? this.#minicourseData.id : this.#lessonId;

    const utmMedium = "P4W";
    const utmSource = this.#partnerId;
    const utmCampaign = contentId;
    const referrer = (this.#partnerId && contentId) ? (this.#partnerId + "-" + contentId) : null;

    let url = "https://www.yourprimer.com";
    url += "?" + "utm_medium=" + utmMedium;
    if (utmSource) {
      url += "&utm_source=" + utmSource;
    }
    if (utmCampaign) {
      url += "&utm_campaign=" + utmCampaign;
    }
    if (referrer) {
      url += "&referrer=" + referrer;
    }
    return url;
  }
}

// Dev convenience:
if (! window.Primer) {
  window.Primer = {};
}
window.Primer.Global = Global;
